import { TITLE_PAGE } from '#shared/constant/constant'
import type { CarItem } from '#shared/interfaces/brands'

/** メーカータブ(カタログ検索の先頭スライド)のタブ名 */
export const MAKER_TAB_TITLE = 'maker'

/** ボディタイプ系タブが持つ値の契約。要素の記載漏れを型で検出するために使う */
interface BodyTypeTabDefinition {
  /** 画面表示用のタブ名 */
  readonly name: string
  /** コード上でタブを識別する内部名。遷移クエリ tab_title にも使う */
  readonly title: string
  /** body-types API の use_type。ボディタイプタブは全件対象のため付けない */
  readonly useType: string | undefined
  /** 車種一覧・ボディタイプ選択へ渡す fuel_cd。ボディタイプタブは燃料で絞らない */
  readonly fuelCd: string | undefined
  /** アイコンのサブディレクトリ(assets/image/body-type/ 配下、末尾スラッシュ込み) */
  readonly iconDir: string
  /** ボディタイプ選択画面のタイトル */
  readonly pageTitle: string
}

/**
 * ボディタイプ系タブの定義。
 * 配列の並びがカタログ検索のスライドの並び(メーカータブの次)と一致する。
 *
 * タブごとに異なる値をここへ集約しているため、燃料違いのタブを増やす場合も
 * スライド・API の use_type・遷移クエリの fuel_cd・アイコンのサブディレクトリ・
 * ボディタイプ選択画面のタイトルへ、この配列への1要素追加だけで反映される。
 * ただし pageTitle が参照する TITLE_PAGE のエントリと、iconDir に対応する
 * アイコン素材(app/assets/image/body-type/ 配下)は別途用意する必要がある。
 */
export const BODY_TYPE_TABS = [
  {
    name: 'ボディタイプ',
    title: 'bodytype',
    useType: undefined,
    fuelCd: undefined,
    iconDir: '',
    pageTitle: TITLE_PAGE.body_type_selection,
  },
  {
    name: 'ハイブリッド',
    title: 'hybrid',
    useType: 'hybrid',
    fuelCd: '1,4,10,15',
    iconDir: 'hv/',
    pageTitle: TITLE_PAGE.hybrid,
  },
  {
    name: 'EV',
    title: 'ev',
    useType: 'ev',
    fuelCd: '9',
    iconDir: 'ev/',
    pageTitle: TITLE_PAGE.ev,
  },
  {
    name: 'PHEV',
    title: 'plugin_hybrid',
    useType: 'plugin_hybrid',
    fuelCd: '11,12,16',
    iconDir: 'phev/',
    pageTitle: TITLE_PAGE.plugin_hybrid,
  },
] as const satisfies readonly BodyTypeTabDefinition[]

/** ボディタイプ系タブの定義1件。外部からは getBodyTypeTab / findBodyTypeTab 経由で扱う */
type BodyTypeTab = (typeof BODY_TYPE_TABS)[number]

/** ボディタイプ系タブのタブ名。メーカータブを含まないため props の型付けに使う */
export type BodyTypeTabTitle = BodyTypeTab['title']

/**
 * ボディタイプ系タブ名から定義を引く。
 * 引数を BodyTypeTabTitle に限定しており BODY_TYPE_TABS が全要素を網羅するため必ず見つかる。
 * (?? の分岐は到達しないが、非 null アサーションを使わずに戻り値を非 undefined にするために置く)
 */
export const getBodyTypeTab = (title: BodyTypeTabTitle): BodyTypeTab =>
  BODY_TYPE_TABS.find((tab) => tab.title === title) ?? BODY_TYPE_TABS[0]

/**
 * カタログ検索のタブ定義。配列の並びがスライドの並びと一致する。
 * 先頭がメーカータブで以降が BODY_TYPE_TABS という構成が、
 * スライド位置(swiper の activeIndex)の唯一の定義元になる。
 */
export const CATALOG_SEARCH_TABS = [
  { name: 'メーカー', title: MAKER_TAB_TITLE },
  ...BODY_TYPE_TABS,
] as const

/** カタログ検索のタブ名 */
export type CatalogSearchTabTitle =
  (typeof CATALOG_SEARCH_TABS)[number]['title']

/**
 * タブ名からボディタイプ系タブの定義を引く。
 * tab_title クエリのような外部由来の文字列にも使えるよう string を受ける。
 * メーカータブや未知の値の場合は undefined を返す。
 */
export const findBodyTypeTab = (title: string): BodyTypeTab | undefined =>
  BODY_TYPE_TABS.find((tab) => tab.title === title)

export const LOCAL_CAR_CODE: CarItem[] = [
  { brand_cd: 1005, brand_name: 'レクサス' },
  { brand_cd: 1010, brand_name: 'トヨタ' },
  { brand_cd: 1015, brand_name: '日産' },
  { brand_cd: 1020, brand_name: 'ホンダ' },
  { brand_cd: 1025, brand_name: 'マツダ' },
  { brand_cd: 1045, brand_name: 'スバル' },
  { brand_cd: 1040, brand_name: '三菱' },
  { brand_cd: 1055, brand_name: 'スズキ' },
  { brand_cd: 1050, brand_name: 'ダイハツ' },
  { brand_cd: 9999, brand_name: '国産車すべて' },
]

export const FOREIGN_CAR_CODE: CarItem[] = [
  { brand_cd: 2015, brand_name: 'BMW' },
  { brand_cd: 2025, brand_name: 'メルセデス\n・ベンツ' },
  { brand_cd: 2035, brand_name: 'フォルクス\nワーゲン' },
  { brand_cd: 2524, brand_name: 'MINI' },
  { brand_cd: 2010, brand_name: 'アウディ' },
  { brand_cd: 4010, brand_name: 'ボルボ' },
  { brand_cd: 2045, brand_name: 'ポルシェ' },
  { brand_cd: 5514, brand_name: 'ジープ' },
  { brand_cd: 3520, brand_name: 'プジョー' },
  { brand_cd: 3025, brand_name: 'フィアット' },
  { brand_cd: 5508, brand_name: 'シボレー' },
  { brand_cd: 3020, brand_name: 'アルファ\nロメオ' },
  { brand_cd: 3515, brand_name: 'ルノー' },
  { brand_cd: 2550, brand_name: 'ランド\nローバー' },
  { brand_cd: 9999, brand_name: '輸入車すべて' },
]
